import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import camelcaseKeys from 'camelcase-keys';
import pluralize from 'pluralize';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faLink, faBriefcase, faUser } from '@fortawesome/pro-solid-svg-icons';

import AssociatedPropertySelector from 'components/tasks/associated_property_selector';
import AssociatedContactSelector from 'components/tasks/associated_contact_selector';
import HorizontalForm from 'components/form/horizontal_form';
import { searchAllProperties } from 'components/research_property/api';
import { searchContacts } from 'components/contacts/api';
import { filter } from 'lodash';

const ICON_MAP = {
  'Research::Property': faBuilding,
  'Contacts::Person': faUser,
  'Contacts::Company': faBriefcase
};

export default class ConnectionLinkModal extends React.Component {
  static propTypes = {
    availableDatabaseOptions: PropTypes.array.isRequired,
    connectableObject: PropTypes.shape({
      connections: PropTypes.arrayOf(PropTypes.shape({
        entityId: PropTypes.number.isRequired,
        id: PropTypes.number.isRequired
      })),
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired
    }),
    connectableObjects: PropTypes.arrayOf(PropTypes.shape({
      connections: PropTypes.arrayOf(PropTypes.shape({
        entityId: PropTypes.number.isRequired,
        id: PropTypes.number.isRequired
      })),
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired
    })),
    onConnectEntities: PropTypes.func,
    onCopyAndConnect: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired
  }

  state = {
    selectedEntity: null,
    selectedDatabase: null,
    suggestedLinks: [],
    createNewSelected: !this.props.onConnectEntities
  }

  componentDidUpdate(prevProps) {
    if (prevProps.connectableObject == this.props.connectableObject) return;

    if (this.props.connectableObject) {
      if (this.props.onConnectEntities) {
        if (this.props.connectableObject.type === 'Research::Property') {
          searchAllProperties(this.props.connectableObject.name, this.handleInitialSearch);
        } else {
          searchContacts({ term: this.props.connectableObject.name }, this.handleInitialSearch);
        }
      } else {
        this.setState({ createNewSelected: true });
      }
    }
  }

  connectionIds = () => {
    const { connectableObject } = this.props;
    if (!connectableObject) return [];

    return (connectableObject.connections || []).map(c => c.entityId);
  }

  alreadyLinkedObjects = () => {
    const { connectableObjects } = this.props;

    if (!connectableObjects) return [];

    return connectableObjects.filter(o => o.connections.length > 0);
  }

  closeModal = () => {
    this.setState({
      selectedEntity: null,
      selectedDatabase: null,
      createNewSelected: false
    });
    this.props.onHide();
  }

  databaseSelected = (e) => {
    this.setState({ selectedDatabase: e.target.value });
  }

  entitySelected = (selectedEntity) => {
    this.setState({ selectedEntity: camelcaseKeys(selectedEntity) });
  }

  createNewSelected = () => {
    this.setState({ createNewSelected: true });
  }

  handleInitialSearch = (suggestions) => {
    const connectionIds = this.connectionIds();

    if (connectionIds) {
      let filteredResults = filter(suggestions, p => !connectionIds.includes(p.id));
      if (this.props.connectableObject.type !== 'Research::Property') {
        filteredResults = filter(filteredResults, { 'contactType': this.props.connectableObject.type });
      }
      this.setState({ suggestedLinks: camelcaseKeys(filteredResults) });
      return;
    }
    this.setState({ suggestedLinks: camelcaseKeys(suggestions) });
  }

  linkToDatabase = () => {
    const { selectedDatabase } = this.state;
    const { connectableObject, connectableObjects, onCopyAndConnect } = this.props;

    if (connectableObjects) {
      onCopyAndConnect(
        selectedDatabase,
        connectableObjects.filter(co => co.connections.length === 0).map(co => [co.type, co.id])
      ).then(this.closeModal);
    } else {
      onCopyAndConnect(selectedDatabase, connectableObject.type, connectableObject.id).then(this.closeModal);
    }
  }

  canLinkToDatabase = () => !!this.state.selectedDatabase

  allConnectableObjectsAlreadyLinked = () => (
    this.props.connectableObjects && this.alreadyLinkedObjects().length === this.props.connectableObjects.length
  )

  canLinkToEntity = () => this.state.selectedEntity && this.state.selectedEntity.associatedModelId

  linkToEntity = () => {
    const { associatedModelId, associatedModelType } = this.state.selectedEntity;

    this.props.onConnectEntities(
      associatedModelType, associatedModelId, this.props.connectableObject.type, this.props.connectableObject.id
    ).then(this.closeModal);
  }

  linkSuggestion = (suggestion) => {
    const associatedModelId = suggestion.id;
    const associatedModelType = suggestion['contactType'] || 'Research::Property';

    this.props.onConnectEntities(
      associatedModelType, associatedModelId, this.props.connectableObject.type, this.props.connectableObject.id
    ).then(this.closeModal);
  }

  renderSuggestions() {
    return this.state.suggestedLinks.map(suggestion => (
      <div className="flex-grow bottom-buffer-sm" key={suggestion.id} name="suggestionRow">
        <div className="entity row-eq-height">
          <div className="col right-buffer-sm">
            <FontAwesomeIcon
              className="type-icon"
              icon={ICON_MAP[this.props.connectableObject.type]}
              pull="left"
              size="lg"
            />
          </div>
          <div className="col">
            <div>
              <strong>{suggestion.name}</strong>
            </div>
            <small>Source: {suggestion.databaseName}</small>
          </div>
          <div className="col grow">
            <div
              className="text-primary clickable pull-right"
              name="link"
              onClick={() => this.linkSuggestion(suggestion)}
            >
              <FontAwesomeIcon className="mr-2" icon={faLink}/> Link
            </div>
          </div>
        </div>
      </div>
    ));
  }

  renderPropertyPicker() {
    return (
      <AssociatedPropertySelector
        createText="Create Link"
        excludedPropertyIds={this.connectionIds()}
        onCreateClicked={this.createNewSelected}
        onModelAdded={this.entitySelected}
      />
    );
  }

  renderContactPicker(type) {
    return (
      <AssociatedContactSelector
        createText="Create Link"
        limitToType={type}
        onCreateContact={this.createNewSelected}
        onModelAdded={this.entitySelected}
      />
    );
  }

  renderPicker() {
    switch (this.props.connectableObject.type) {
    case 'Research::Property':
      return this.renderPropertyPicker();
    case 'Contacts::Person':
      return this.renderContactPicker('Contacts::Person');
    case 'Contacts::Company':
      return this.renderContactPicker('Contacts::Company');
    }
  }

  renderForm() {
    const { createNewSelected, selectedEntity, suggestedLinks } = this.state;

    if (createNewSelected || this.props.connectableObjects) return this.renderDatabasePicker();

    const typeLabel = this.props.connectableObject.type === 'Research::Property' ? 'Database Properties' : 'Contacts';
    return (
      <div>
        {suggestedLinks.length > 0 && (
          <div className="bottom-buffer-md">
            <h4 className="text-muted">Suggested {typeLabel}</h4>
            {this.renderSuggestions()}
          </div>
        )}
        {this.renderPicker()}
        {selectedEntity && selectedEntity.associatedModelId && (
          <div>This project will be linked to "{selectedEntity.name}"</div>
        )}
      </div>
    );
  }

  renderSaveButton() {
    if (this.allConnectableObjectsAlreadyLinked()) {
      return <button className="btn btn-primary" name="close" onClick={this.props.onHide}>Close</button>;
    }

    if (this.state.createNewSelected || this.props.connectableObjects) {
      return (
        <button className="btn btn-save" disabled={!this.canLinkToDatabase()} name="save" onClick={this.linkToDatabase}>
          Save
        </button>
      );
    }

    return (
      <button className="btn btn-primary" disabled={!this.canLinkToEntity()} name="save" onClick={this.linkToEntity}>
        Link
      </button>
    );
  }

  renderDatabasePicker() {
    if (this.allConnectableObjectsAlreadyLinked()) {
      return (
        <div className="mb-2">
          <strong>All the selected Leads were already saved to a Database</strong>
        </div>
      );
    }

    const objectCount = (this.props.connectableObjects || []).length;
    const objectString = objectCount > 1 ? `These ${objectCount} objects` : 'This object';

    return (
      <React.Fragment>
        <div className="mb-2">
          <strong>{`${objectString} will be saved to the database you select.`}</strong>
        </div>
        <HorizontalForm>
          <HorizontalForm.Select
            includeBlank="select a database"
            label="Database"
            name="databasePicker"
            options={this.props.availableDatabaseOptions}
            required
            onChange={this.databaseSelected}
          />
        </HorizontalForm>
        {this.renderConnectionNumbers()}
      </React.Fragment>
    );
  }

  renderConnectionNumbers() {
    const { availableDatabaseOptions, connectableObjects } = this.props;
    const { selectedDatabase } = this.state;
    if (!(connectableObjects && selectedDatabase)) return [];

    const linkedCount = this.alreadyLinkedObjects().length;
    const objectCount = connectableObjects.length;
    const databaseName = availableDatabaseOptions.find(dbs => dbs[1].toString() === selectedDatabase)[0];
    const str = `${pluralize('Leads', objectCount - linkedCount, true)} will be added to ${databaseName}.`;

    if (linkedCount === 0) return str;

    return str.concat(
      ` ${pluralize('Leads', linkedCount, true)} ${pluralize('was', linkedCount)} already saved to a Database.`
    );
  }

  render() {
    const { connectableObject, connectableObjects } = this.props;

    return (
      <Modal
        className="overflow-visible"
        show={!!(connectableObject || connectableObjects)}
        onHide={this.closeModal}
      >
        <Modal.Header closeButton>Create Database Link</Modal.Header>
        <Modal.Body>
          {(connectableObject || connectableObjects) && (
            <React.Fragment>
              {this.renderForm()}
              <div className="text-right mt-4">{this.renderSaveButton()}</div>
            </React.Fragment>
          )}
        </Modal.Body>
      </Modal>
    );
  }
}
